import { apiRequest, apiRequestWithEtag, type IEtagged } from '@/domains/api';
import type { IContentEvent, IContentEventBody } from './event-contracts';
import type { ContentPostStatus } from './post-contracts';

/**
 * survey-api's events — `/api/v1/admin/website/events` — and the sole reader of them.
 *
 * It follows `posts-api.ts` and `team-api.ts` in everything the three surfaces share: the
 * revision travels as an opaque `ETag` quoted back in `If-Match`, the server filters, and nothing
 * here decides whether an event may be published.
 *
 * **There is no route for an agenda item or a speaker**, and that is survey-api's design rather
 * than an omission: both lists are members of the event's body, so `save` carries the programme
 * it means. Adding, removing, reordering and retiming are therefore one write that cannot
 * half-apply — which three routes, each with a revision of its own, could.
 */

const BASE = '/api/v1/admin/website/events';

function eventPath(eventId: string): string {
  return `${BASE}/${encodeURIComponent(eventId)}`;
}

function precondition(etag: string): { 'If-Match': string } {
  return { 'If-Match': etag };
}

export const eventsApi = {
  /** Every event: soonest first, the archived after them. */
  list(status?: ContentPostStatus, signal?: AbortSignal): Promise<IContentEvent[]> {
    return apiRequest<IContentEvent[]>(`${BASE}${status ? `?status=${status}` : ''}`, { signal });
  },

  read(eventId: string, signal?: AbortSignal): Promise<IEtagged<IContentEvent>> {
    return apiRequestWithEtag<IContentEvent>(eventPath(eventId), { signal });
  },

  /** Stands one up as a draft. */
  create(body: IContentEventBody): Promise<IEtagged<IContentEvent>> {
    return apiRequestWithEtag<IContentEvent>(BASE, { method: 'POST', body });
  },

  /** The event and its whole programme. */
  save(eventId: string, etag: string, body: IContentEventBody): Promise<IEtagged<IContentEvent>> {
    return apiRequestWithEtag<IContentEvent>(eventPath(eventId), {
      method: 'PUT',
      body,
      headers: precondition(etag)
    });
  },

  publish(eventId: string, etag: string): Promise<IEtagged<IContentEvent>> {
    return apiRequestWithEtag<IContentEvent>(`${eventPath(eventId)}/publication`, {
      method: 'POST',
      headers: precondition(etag)
    });
  },

  /** WEB-08 AC4 — off the live listing, every word and every version kept. */
  archive(eventId: string, etag: string): Promise<IEtagged<IContentEvent>> {
    return apiRequestWithEtag<IContentEvent>(`${eventPath(eventId)}/publication`, {
      method: 'DELETE',
      headers: precondition(etag)
    });
  },

  /**
   * Brings an archived event back **as a draft** — the only way back.
   *
   * Not onto the site: survey-api refuses to publish an archived event, because what is wrong
   * with one cannot be fixed while it is archived and a return that published would put the
   * wrong version up in order to correct it. It is `content.author`: a draft is not public.
   */
  restore(eventId: string, etag: string): Promise<IEtagged<IContentEvent>> {
    return apiRequestWithEtag<IContentEvent>(`${eventPath(eventId)}/restoration`, {
      method: 'POST',
      headers: precondition(etag)
    });
  },

  /** Restores a version's content — the programme with it, since the agenda is part of it. */
  revert(eventId: string, etag: string, versionNo: number): Promise<IEtagged<IContentEvent>> {
    return apiRequestWithEtag<IContentEvent>(`${eventPath(eventId)}/reverts`, {
      method: 'POST',
      body: { versionNo },
      headers: precondition(etag)
    });
  }
};
